import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkedAlt, faTimes, faList } from "@fortawesome/free-solid-svg-icons";

import './styles.css'
import Maps, { isMapsEnabled } from '../../../components/maps'
import Header from '../../components/header'
import Footer from '../../components/footer'
import List, { PAGE_SIZE, companyKey } from './List'
import CompanyPanel from './CompanyPanel'
import { detailsPath } from './companyFormat'
import Location from './Location'
import { getCompaniesByCity, handleFilters, removeFilter, openMap } from '../../../store/actions/provider'

const DESKTOP_QUERY = '(min-width: 1025px)'
// Estado da lista guardado ao abrir o detalhe no celular, para a volta cair no mesmo ponto.
const RETURN_KEY = 'sr-companies-return'

const readReturnState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(RETURN_KEY)) || null
  } catch (err) {
    return null
  }
}

function useIsDesktop() {
  const query = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null
  const [isDesktop, setIsDesktop] = useState(query ? query.matches : false)
  useEffect(() => {
    if (!query) return undefined
    const update = () => setIsDesktop(query.matches)
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [query])
  return isDesktop
}

// Enquanto a busca responde: linhas no formato da lista, sem cobrir a tela.
const ListSkeleton = () => (
  <div className='cp-list-panel' aria-hidden='true'>
    {[0, 1, 2, 3, 4, 5].map(i => (
      <div key={i} className='cp-row cp-row-skeleton'>
        <span className='cp-row-main'>
          <span className='cp-skeleton cp-skeleton-name' />
          <span className='cp-skeleton cp-skeleton-meta' />
          <span className='cp-skeleton cp-skeleton-address' />
        </span>
      </div>
    ))}
  </div>
)

function Companies() {
  const dispatch = useDispatch()
  const { provider } = useSelector(state => state)
  const { selectedCity, companiesFiltered, filters, companies, filtersVisible, maps, loading } = provider
  const history = useHistory()
  const isDesktop = useIsDesktop()
  const [returnState] = useState(readReturnState)
  const [selected, setSelected] = useState(null)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  useEffect(() => {
    dispatch(getCompaniesByCity())
  }, [selectedCity, dispatch])

  // Desktop: o painel mostra a oficina escolhida; sem escolha (ou se ela saiu da lista
  // por um filtro), a primeira do resultado.
  useEffect(() => {
    if (!isDesktop || loading) return
    const stillListed = selected && companiesFiltered.some(c => companyKey(c) === companyKey(selected))
    if (!stillListed) setSelected(companiesFiltered[0] || null)
  }, [isDesktop, loading, companiesFiltered, selected])

  // Volta do detalhe (celular): restaura a rolagem depois que a lista aparece.
  useEffect(() => {
    if (!returnState || loading || !companiesFiltered.length) return
    window.scrollTo(0, returnState.scrollY || 0)
    sessionStorage.removeItem(RETURN_KEY)
  }, [returnState, loading, companiesFiltered.length])

  const handleSelect = useCallback(item => {
    if (isDesktop) {
      setSelected(item)
      return
    }
    try {
      sessionStorage.setItem(RETURN_KEY, JSON.stringify({ scrollY: window.scrollY, visible: visibleCount }))
    } catch (err) {
      // Sem sessionStorage a volta so nao restaura a posicao.
    }
    history.push(detailsPath(item), { fromSearch: true })
  }, [isDesktop, history, visibleCount])

  const selectedKey = useMemo(() => (isDesktop && selected ? companyKey(selected) : null), [isDesktop, selected])

  const handleRemoveFilter = async (item, type) => {
    await dispatch(removeFilter(item, type, filters))

    dispatch(handleFilters(companies, filters))
  }

  const toggleMap = () => dispatch(openMap(maps, 'maps'))

  const activeFilters = filtersVisible
    ? Object.keys(filters).flatMap(type => filters[type].map(item => ({ item, type })))
    : []
  const hasResults = companiesFiltered.length > 0
  const resultCount = companiesFiltered.length
  const hasLocations = isMapsEnabled() && companiesFiltered.some(c => Number(c.latitude) && Number(c.longitude))
  const showMap = maps && hasLocations
  const showPanel = isDesktop && hasResults && !showMap && !loading

  return (
    <div className='cp-page'>
      <Header contained />
      <main className='cp-main'>
        <Location />

        <div className='cp-container'>
          {selectedCity?.Name &&
            <div className='cp-head'>
              <div>
                <h1>
                  {loading
                    ? 'Buscando oficinas e autopeças…'
                    : <>{resultCount} {resultCount === 1 ? 'oficina ou autopeça' : 'oficinas e autopeças'} em <span>{selectedCity.Name}</span></>
                  }
                </h1>
                {!loading && hasResults && <p>Toque numa empresa para ver serviços, avaliações e contato.</p>}
              </div>
              {/* Sem chave do Google Maps configurada, o botao nem aparece (o mapa so mostraria erro). */}
              {isMapsEnabled() &&
                <button
                  type='button'
                  className='cp-map-toggle'
                  onClick={toggleMap}
                  disabled={!hasLocations}
                  title={hasLocations ? '' : 'Nenhuma empresa desta busca tem localização no mapa'}
                >
                  <FontAwesomeIcon icon={maps ? faList : faMapMarkedAlt} aria-hidden='true' />
                  {maps ? 'Ver só a lista' : 'Ver no mapa'}
                </button>
              }
            </div>
          }

          {activeFilters.length > 0 &&
            <ul className='cp-chips' aria-label='Filtros aplicados'>
              {activeFilters.map(({ item, type }) =>
                <li key={`${type}-${item.id}`}>
                  {item.description}
                  <button
                    type='button'
                    onClick={() => handleRemoveFilter(item, type)}
                    aria-label={`Remover filtro ${item.description}`}
                  >
                    <FontAwesomeIcon icon={faTimes} aria-hidden='true' />
                  </button>
                </li>
              )}
            </ul>
          }

          <div className={`cp-results${showMap || showPanel ? ' cp-results-split' : ''}`}>
            {showMap &&
              <div className='cp-map'>
                <Maps list={companiesFiltered} city={selectedCity.Name} states={selectedCity.States.Initial} />
              </div>
            }
            {showPanel && <CompanyPanel selected={selected} />}
            {selectedCity?.Name
              ? (loading
                ? <ListSkeleton />
                : <List
                  list={companiesFiltered}
                  selectedKey={selectedKey}
                  onSelect={handleSelect}
                  initialVisible={returnState ? returnState.visible : PAGE_SIZE}
                  onVisibleChange={setVisibleCount}
                />)
              : <p className='cp-empty'>Escolha uma cidade para ver as oficinas, autocenters e autopeças da região.</p>
            }
          </div>
        </div>
      </main>

      <Footer contained />

    </div>
  )
}

export default Companies;
